/**
 * src/api/printing/errors.ts — PrintError discriminé pour le module PrintingApi.
 *
 * Union discriminée sur `type`. Chaque variant est exhaustif et testable.
 * Réutilise le pattern Result de src/api/result.ts.
 *
 * Invariant 4.B.4 : 0 any.
 * Invariant 4.A.15 : aucun PII (nom client, email) dans les messages d'erreur.
 */

import type { AppError } from '@/api/errors';

// ---------------------------------------------------------------------------
// Union PrintError
// ---------------------------------------------------------------------------

/** Erreur de validation d'input Zod. */
type InvalidInputError = {
  readonly type: 'invalid_input';
  readonly field: string;
  readonly message: string;
};

/** Vente introuvable en DB (saleId inconnu ou accès RLS refusé). */
type SaleNotFoundError = {
  readonly type: 'sale_not_found';
  readonly saleId: string;
};

/** Aucune imprimante connectée ou détectée. */
type NoPrinterConnectedError = {
  readonly type: 'no_printer_connected';
};

/** L'imprimante n'a pas répondu dans le délai imparti. */
type PrinterTimeoutError = {
  readonly type: 'printer_timeout';
};

/**
 * Erreur matérielle ou ESC/POS retournée par l'imprimante.
 * code = code erreur du bridge natif (ex: "E_BT_DISCONNECTED").
 */
type PrinterError = {
  readonly type: 'printer_error';
  readonly code: string;
  readonly message: string;
};

/**
 * Erreur de formatage du ticket (ex: TicketData malformée, champ obligatoire absent).
 * cause = message d'erreur technique (sans PII).
 */
type FormatError = {
  readonly type: 'format_error';
  readonly cause: string;
};

/**
 * Erreur de la couche API (repo) — propagée depuis src/api/errors.ts.
 * Wraps AppError pour isoler la dépendance au type générique.
 */
type RepoError = {
  readonly type: 'repo_error';
  readonly cause: AppError;
};

/** Union discriminée de toutes les erreurs d'impression. */
export type PrintError =
  | InvalidInputError
  | SaleNotFoundError
  | NoPrinterConnectedError
  | PrinterTimeoutError
  | PrinterError
  | FormatError
  | RepoError;

// ---------------------------------------------------------------------------
// Helpers de construction
// ---------------------------------------------------------------------------

export function invalidInputError(field: string, message: string): PrintError {
  return { type: 'invalid_input', field, message };
}

export function saleNotFoundError(saleId: string): PrintError {
  return { type: 'sale_not_found', saleId };
}

export function noPrinterConnectedError(): PrintError {
  return { type: 'no_printer_connected' };
}

export function printerTimeoutError(): PrintError {
  return { type: 'printer_timeout' };
}

export function printerError(code: string, message: string): PrintError {
  return { type: 'printer_error', code, message };
}

export function formatError(cause: string): PrintError {
  return { type: 'format_error', cause };
}

export function repoError(cause: AppError): PrintError {
  return { type: 'repo_error', cause };
}

// ---------------------------------------------------------------------------
// Message UI lisible par l'opérateur (sans PII)
// ---------------------------------------------------------------------------

/**
 * Retourne un message français lisible par l'opérateur caisse.
 * Ne contient jamais de PII (nom client, email, etc.) — invariant 4.A.15.
 */
export function printErrorMessage(error: PrintError): string {
  switch (error.type) {
    case 'invalid_input':
      return `Données invalides (${error.field}) : ${error.message}`;
    case 'sale_not_found':
      return `Vente introuvable. Vérifiez que la vente existe et est accessible.`;
    case 'no_printer_connected':
      return 'Aucune imprimante connectée. Vérifiez la connexion Bluetooth ou réseau.';
    case 'printer_timeout':
      return "L'imprimante n'a pas répondu. Vérifiez qu'elle est allumée et prête.";
    case 'printer_error':
      return `Erreur imprimante${error.message ? ` — ${error.message}` : ''}.`;
    case 'format_error':
      return `Erreur de formatage du ticket. Contactez le support.`;
    case 'repo_error':
      return `Erreur de chargement des données. Réessayez.`;
  }
}
